import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import DatePicker, { DateObject } from "react-multi-date-picker";
import AsyncSelect from "react-select/async";

import {
  BookingHeader,
  BookingInfoCards,
  BookingSummary,
  FlightHeader,
  PAX_TYPES,
  bookingCss,
  formatINR,
  loadDraft,
  saveDraft,
} from "../../components/flight-booking/BookingShared";
import { countriesService } from "../../services/countries.service";

const countrySelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 .2rem rgba(13,110,253,.15)" : "none",
    fontSize: 14,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const loadCountryOptions = async (inputValue) => {
  const search = (inputValue || "").trim();
  if (search.length < 2) return [];

  try {
    const res = await countriesService.list({
      page: 1,
      status: "active",
      search,
    });
    if (!res?.success) {
      toast.error(res?.message || "Failed to load countries.");
      return [];
    }

    return (res.resData?.data || []).map((country) => ({
      value: country.name,
      label: country.name,
    }));
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Failed to load countries.");
    return [];
  }
};

// Date-of-birth window per passenger type, counted back from the travel date
const dobLimits = (typeKey, travelDate) => {
  const fmt = (m) => m.format("YYYY-MM-DD");
  const travel = moment(travelDate, "YYYY-MM-DD");
  const ref = travel.isValid() ? travel : moment();
  const today = moment();
  if (typeKey == "adults") return { min: fmt(ref.clone().subtract(120, "years")), max: fmt(ref.clone().subtract(12, "years")) };
  if (typeKey == "children") return { min: fmt(ref.clone().subtract(12, "years").add(1, "day")), max: fmt(ref.clone().subtract(2, "years")) };
  return { min: fmt(ref.clone().subtract(2, "years").add(1, "day")), max: fmt(moment.min(ref, today)) };
};

// The calendar opens on the latest allowed birth date (Adult: 12 years before travel, Child: 2 years, Infant: travel date).
// One stable object per passenger type, so the calendar is not re-centred on every render.
const openOnCache = {};
const dobOpenOn = (typeKey, travelDate) => {
  const { max } = dobLimits(typeKey, travelDate);
  if (openOnCache[typeKey]?.max != max) openOnCache[typeKey] = { max, date: new DateObject({ date: max, format: "YYYY-MM-DD" }) };
  return openOnCache[typeKey].date;
};

const PAX_META = {
  adults: { icon: "bi-person", note: "Passenger must be 12 years or older." },
  children: { icon: "bi-emoji-smile", note: "Passenger must be between 2 and 12 years." },
  infants: { icon: "bi-balloon-heart", note: "Passenger must be below 2 years." },
};

const GENDER_BY_TITLE = { Mr: "male", Master: "male", Mrs: "female", Ms: "female", Miss: "female" };

const NAME_RE =/^[A-Za-z][A-Za-z .'-]*$/;
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const FlightBooking = () => {  
  const location = useLocation();
  const navigate = useNavigate();
  const draft = location.state?.fare ? location.state : loadDraft();
  const fare = draft?.fare;
  const search = draft?.search;
  const pax = draft?.pax || search?.pax_detail || { adults: 1, children: 0, infants: 0 };
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    loadCountryOptions();
  }, []);
   const loadCountryOptions = async () => {
      try {
        const res = await countriesService.list({ page: 1, status: "active" });
        setCountryOptions((res?.resData?.data || []).map(c => ({ value: c.code, label: c.name })));
      } catch (err) {
        setCountryOptions([]);
      }
    };

  const [passengers, setPassengers] = useState(() =>
    draft?.passengers?.length
      ? draft.passengers.map((p) => ({ ...p, type: PAX_TYPES.find((t) => t.key == p.type.key) }))
      : PAX_TYPES.flatMap((t) =>
          Array.from({ length: pax[t.key] || 0 }, (_, i) => ({
            id: `${t.key}-${i}`,
            type: t,
            index: i + 1,
            title: "",
            gender: "",
            first: "",
            last: "",
            dob: "",
            wheelchair: "No",
            has_passport: false,
            passport_number: "",
            nationality: "",
            passport_issuing_country: "",
            passport_issue_date: "",
            passport_expiry: "",
            ticket_price: fare?.fare?.[t.fareKey]?.total_fare ?? 0,
          }))
        )
  );
  
  const [gstOn, setGstOn] = useState(!!draft?.gst);
  const [gst, setGst] = useState(draft?.gst || { number: "", company: "" });
  const [errors, setErrors] = useState({});

  const travelDate = fare?.origin?.date;
  const seats = fare?.available_seats;
  const seatsRequired = (pax.adults || 0) + (pax.children || 0); // infants travel on an adult's lap

  if (!fare) return <Navigate to="/flight-search" replace />;

  const setField = (id, field, value) => {
    setPassengers((prev) => prev.map((p) => (p.id == id ? { ...p, [field]: value } : p)));
    setErrors((prev) => ({ ...prev, [`${id}.${field}`]: undefined }));
  };

  // Choosing a title also picks the matching gender (it can still be changed afterwards)
  const handleTitleChange = (id, title) => {
    setPassengers((prev) =>
      prev.map((p) => (p.id == id ? { ...p, title, gender: GENDER_BY_TITLE[title] || p.gender } : p))
    );
    setErrors((prev) => ({ ...prev, [`${id}.title`]: undefined, [`${id}.gender`]: undefined }));
  };

  // Clear everything the user entered for one traveller (ticket price comes from the fare, so it stays)
  const resetPassenger = (id) => {
    setPassengers((prev) =>
      prev.map((p) =>
        p.id == id
          ? {
              ...p,
              title: "",
              gender: "",
              first: "",
              last: "",
              dob: "",
              wheelchair: "No",
              nationality: "",
              has_passport: false,
              passport_number: "",
              passport_issuing_country: "",
              passport_issue_date: "",
              passport_expiry: "",
            }
          : p
      )
    );
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`${id}.`) && !k.startsWith(`${id}-`))));
  };

  const validate = () => {
    const next = {};
    passengers.forEach((p) => {
      if (!p.title) next[`${p.id}.title`] = "Select title";
      if (!p.gender) next[`${p.id}.gender`] = "Select gender";
      if (!p.first.trim()) next[`${p.id}.first`] = "Enter first name";
      else if (!NAME_RE.test(p.first.trim())) next[`${p.id}.first`] = "Letters only";
      if (!p.last.trim()) next[`${p.id}.last`] = "Enter last name";
      else if (!NAME_RE.test(p.last.trim())) next[`${p.id}.last`] = "Letters only";
      const { min, max } = dobLimits(p.type.key, travelDate);
      // if (!p.dob) next[`${p.id}.dob`] = "Enter date of birth";
      // else if (p.dob < min || p.dob > max) next[`${p.id}.dob`] = `${p.type.label} must be ${p.type.hint.toLowerCase()}`;
      if (p.has_passport && p.passport_issue_date && p.passport_issue_date > moment().format("YYYY-MM-DD")) {
        next[`${p.id}.passport_issue_date`] = "Issue date cannot be in the future";
      }
      if (p.has_passport && p.passport_expiry) {
        const earliestExpiry = p.passport_issue_date || moment().format("YYYY-MM-DD");
        if (p.passport_expiry < earliestExpiry) {
          next[`${p.id}.passport_expiry`] = p.passport_issue_date
            ? "Expiry date must be after the issue date"
            : "Expiry date must be today or later";
        }
      }
    });
    if (gstOn) {
      if (!GST_RE.test(gst.number.trim().toUpperCase())) next.gstNumber = "Enter a valid 15-character GSTIN";
      if (!gst.company.trim()) next.gstCompany = "Enter company name";
    }
    setErrors(next);
    return Object.keys(next).length == 0;
  };

  const handleContinue = () => {
    if (seats != null && seatsRequired > seats) {
      return toast.error(`Only ${seats} seat${seats == 1 ? "" : "s"} available for ${seatsRequired} passengers`);
    }
    if (!validate()) return toast.error("Please complete the highlighted fields");
    const next = { ...draft, fare, search, pax, passengers, gst: gstOn ? { ...gst, number: gst.number.trim().toUpperCase() } : null };
    saveDraft(next);
    navigate("/flight-booking/review", { state: next });
  };

  const fieldClass = (key) => `form-control fb-input ${errors[key] ? "is-invalid" : ""}`;

  return (
    <div className="fb-page">
      <style>{bookingCss}</style>
      <style>{`
        .fb-pax-grid.fb-traveller-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .fb-passport-toggle { display:flex; align-items:center; gap:8px; margin-top:14px; font-size:13px; font-weight:500; color:#0f172a; cursor:pointer; }
        .fb-passport-box { margin-top:12px; padding:16px; border-radius:14px; background:#f1f5f9; border:1px solid #e2e8f0; }
        .fb-passport-title { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:var(--btn-bg); margin-bottom:12px; }
        .fb-passport-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; }
        .fb-reset-btn { background:none; border:none; padding:4px 6px; font-size:13px; font-weight:500; color:#64748b; display:inline-flex; align-items:center; gap:6px; }
        .fb-reset-btn:hover { color:var(--btn-bg); }
        .fb-group-head { display:flex; align-items:center; gap:8px; margin:14px 0 8px; font-size:14px; font-weight:700; color:#0f172a; }
        .fb-group-head i { color:var(--btn-bg); }
        .fb-name-note { display:flex; align-items:center; gap:10px; padding:8px 14px; font-weight:400; border-radius:12px; background:#fff7ed; border:1px solid #fed7aa; color:#9a3412; font-size:13px; }
        @media (max-width: 991px) { .fb-pax-grid.fb-traveller-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 575px) {
          .fb-pax-grid.fb-traveller-grid, .fb-passport-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <BookingHeader
        fare={fare}
        pax={pax}
        step={1}
        onBack={() => navigate("/flight-search", { state: { restore: true } })}
      />

      <div className="row g-3 mt-0">
        {/* Left column */}
        <div className="col-12 col-lg-8">
          <FlightHeader fare={fare} search={search} />
          <BookingInfoCards fare={fare} />

          <div className="fb-section-head d-flex flex-wrap align-items-center gap-2">
            <span><i className="bi bi-people me-2"></i>Traveler Details</span>
            <span className="fb-name-note ms-lg-auto">
              <i className="bi bi-info-circle"></i>
              <span><b>Important:</b> Enter name as mentioned on your passport or Government approved IDs.</span>
            </span>
          </div>
          {passengers.map((p) => (
            <React.Fragment key={p.id}>
            {p.index == 1 && (
              <div className="fb-group-head">
                <i className={`bi ${PAX_META[p.type.key].icon}`}></i> {p.type.label.toUpperCase()} ({p.type.hint})
              </div>
            )}
            <div className="fb-card">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="fb-avatar">{p.index}</span>
                <span className="fb-strong" style={{ display: "flex", flexDirection: "column", fontWeight: 600, gap: "0px" }}>
                  {p.type.label.toUpperCase()} {p.index}
                  <span style={{ fontSize: 12, fontWeight: 400, color: "#8b8b8b" }}>{p.type.hint}</span>
                </span>
                <button type="button" className="fb-reset-btn ms-auto" onClick={() => resetPassenger(p.id)}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
              <div className="fb-pax-grid fb-traveller-grid">
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-title`}>Title <span className="text-danger">*</span></label>
                  <select id={`${p.id}-title`} className={`form-select fb-input ${errors[`${p.id}.title`] ? "is-invalid" : ""}`} value={p.title} onChange={(e) => handleTitleChange(p.id, e.target.value)}>
                    <option value="">Select Title</option>
                    <option value="Mr">Mr</option><option value="Mrs">Mrs</option><option value="Ms">Ms</option>
                  </select>
                  <div className="invalid-feedback">{errors[`${p.id}.title`]}</div>
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-first`}>First Name <span className="text-danger">*</span></label>
                  <input id={`${p.id}-first`} className={fieldClass(`${p.id}.first`)} placeholder="As on passport" value={p.first} onChange={(e) => setField(p.id, "first", e.target.value)} />
                  <div className="invalid-feedback">{errors[`${p.id}.first`]}</div>
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-last`}>Last Name <span className="text-danger">*</span></label>
                  <input id={`${p.id}-last`} className={fieldClass(`${p.id}.last`)} placeholder="As on passport" value={p.last} onChange={(e) => setField(p.id, "last", e.target.value)} />
                  <div className="invalid-feedback">{errors[`${p.id}.last`]}</div>
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-ticket-price`}>Ticket Price</label>
                  <input id={`${p.id}-ticket-price`} className="form-control fb-input" value={formatINR(p.ticket_price)} readOnly />
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-gender`}>Gender <span className="text-danger">*</span></label>
                  <select id={`${p.id}-gender`} className={`form-select fb-input ${errors[`${p.id}.gender`] ? "is-invalid" : ""}`} value={p.gender || ""} onChange={(e) => setField(p.id, "gender", e.target.value)}>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="invalid-feedback">{errors[`${p.id}.gender`]}</div>
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-dob`}>Date of Birth</label>
                  <DatePicker
                    id={`${p.id}-dob`}
                    onOpenPickNewDate={false}
                    value={p.dob || null}
                    onChange={(d) => setField(p.id, "dob", d ? d.format("YYYY-MM-DD") : "")}
                    format="YYYY-MM-DD"
                    placeholder="Select date"
                    minDate={dobLimits(p.type.key, travelDate).min}
                    maxDate={dobLimits(p.type.key, travelDate).max}
                    currentDate={p.dob ? undefined : dobOpenOn(p.type.key, travelDate)}
                    editable={false}
                    inputClass={fieldClass(`${p.id}.dob`)}
                    containerStyle={{ width: "100%" }}
                    style={{ width: "100%" }}
                  />
                  {errors[`${p.id}.dob`] && <div className="invalid-feedback d-block">{errors[`${p.id}.dob`]}</div>}
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-wheelchair`}>Wheel Chair Required</label>
                  <select id={`${p.id}-wheelchair`} className="form-select fb-input" value={p.wheelchair || "No"} onChange={(e) => setField(p.id, "wheelchair", e.target.value)}>
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                <div className="fb-cell">
                  <label className="fb-label" htmlFor={`${p.id}-nationality`}>Nationality</label>
                  <input
                    id={`${p.id}-nationality`}
                    className={fieldClass(`${p.id}-nationality`)}
                    placeholder="Nationality"
                    value={p.nationality || ""}
                    onChange={(e) => setField(p.id, "nationality", e.target.value)}
                  />
                </div>
              </div>

              <label className="fb-passport-toggle" htmlFor={`${p.id}-has-passport`}>
                <input
                  id={`${p.id}-has-passport`}
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={!!p.has_passport}
                  onChange={(e) => setField(p.id, "has_passport", e.target.checked)}
                />
                Add passport / travel document details
              </label>

              {p.has_passport && (
                <div className="fb-passport-box">
                  <div className="fb-passport-title"><i className="bi bi-journal-text"></i> Passport / Travel Document</div>
                  <div className="fb-passport-grid">
                    <div className="fb-cell">
                      <label className="fb-label" htmlFor={`${p.id}-passport-number`}>Passport Number</label>
                      <input
                        id={`${p.id}-passport-number`}
                        className={fieldClass(`${p.id}.passport_number`)}
                        placeholder="e.g. A1234567"
                        value={p.passport_number || ""}
                        onChange={(e) => setField(p.id, "passport_number", e.target.value)}
                      />
                    </div>
                    <div className="fb-cell">
                      <label className="fb-label" htmlFor={`${p.id}-passport-issue-date`}>Passport Issue Date</label>
                      <DatePicker
                        id={`${p.id}-passport-issue-date`}
                        onOpenPickNewDate={false}
                        value={p.passport_issue_date || null}
                        onChange={(d) => setField(p.id, "passport_issue_date", d ? d.format("YYYY-MM-DD") : "")}
                        format="YYYY-MM-DD"
                        placeholder="yyyy-mm-dd"
                        maxDate={moment().format("YYYY-MM-DD")}
                        editable={false}
                        inputClass={fieldClass(`${p.id}.passport_issue_date`)}
                        containerStyle={{ width: "100%" }}
                        style={{ width: "100%" }}
                      />
                      {errors[`${p.id}.passport_issue_date`] && <div className="invalid-feedback d-block">{errors[`${p.id}.passport_issue_date`]}</div>}
                    </div>
                    <div className="fb-cell">
                      <label className="fb-label" htmlFor={`${p.id}-passport-expiry`}>Passport Expiry Date</label>
                      <DatePicker
                        id={`${p.id}-passport-expiry`}
                        onOpenPickNewDate={false}
                        value={p.passport_expiry || null}
                        onChange={(d) => setField(p.id, "passport_expiry", d ? d.format("YYYY-MM-DD") : "")}
                        format="YYYY-MM-DD"
                        placeholder="yyyy-mm-dd"
                        minDate={p.passport_issue_date || moment().format("YYYY-MM-DD")}
                        editable={false}
                        inputClass={fieldClass(`${p.id}.passport_expiry`)}
                        containerStyle={{ width: "100%" }}
                        style={{ width: "100%" }}
                      />
                      {errors[`${p.id}.passport_expiry`] && <div className="invalid-feedback d-block">{errors[`${p.id}.passport_expiry`]}</div>}
                    </div>
                    <div className="fb-cell">
                      <label className="fb-label" htmlFor={`${p.id}-passport-country`}>Passport Issuing Country</label>
                      <AsyncSelect
                        inputId={`${p.id}-passport-country`}
                        cacheOptions
                        defaultOptions={countryOptions}
                        loadOptions={(inputValue) => {
                          const term = (inputValue || "").trim().toLowerCase();
                          return Promise.resolve(countryOptions.filter((c) => c.label.toLowerCase().includes(term)));
                        }}
                        value={
                          countryOptions.find((o) => o.value === p.passport_issuing_country) ||
                          (p.passport_issuing_country ? { value: p.passport_issuing_country, label: p.passport_issuing_country } : null)
                        }
                        onChange={(option) => setField(p.id, "passport_issuing_country", option?.value || "")}
                        placeholder="Select Country"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={countrySelectStyles}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="fb-muted mt-3">{PAX_META[p.type.key].note}</div>
            </div>
            </React.Fragment>
          ))}
        </div>

        {/* Fare breakdown */}
        <div className="col-12 col-lg-4">
          <BookingSummary fare={fare} search={search} pax={pax}>
            <button type="button" className="fb-primary" onClick={handleContinue}>
              Review &amp; Continue <i className="bi bi-arrow-right ms-1"></i>
            </button>
            <button type="button" className="fb-goback" onClick={() => navigate("/flight-search", { state: { restore: true } })}>
              <i className="bi bi-arrow-left me-2"></i>Go Back
            </button>
          </BookingSummary>
        </div>
      </div>
    </div>
  );
};

export default FlightBooking;
